import { useEffect, useState } from "react";

// Injected at build time by Vite (see vite.config.ts `define`). In local dev,
// or any build made without the APP_VERSION arg, this is the literal "dev",
// which disables the check so it never nags during development.
declare const __APP_VERSION__: string;

const POLL_INTERVAL_MS = 60_000;

async function fetchServerVersion(signal: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch("/api/version", { signal, cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { version?: unknown };
    return typeof data.version === "string" ? data.version : null;
  } catch {
    // Transient network error / offline / aborted — ignore. We only ever flip
    // to "update available" on a positive, differing response, so a failed
    // poll can never produce a false alarm.
    return null;
  }
}

/**
 * Detects when the deployed server is running a newer build than the JS bundle
 * this tab loaded — i.e. a deploy landed while the page stayed open. Returns
 * true once drift is observed (and stays true); the caller surfaces a refresh
 * prompt. We deliberately never reload automatically — that would interrupt
 * whatever the user is mid-way through.
 *
 * Polls once a minute and also re-checks whenever the tab regains focus, which
 * catches the common "left it open overnight, came back" case promptly.
 */
export function useVersionCheck(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (__APP_VERSION__ === "dev") return;

    const controller = new AbortController();

    const check = async () => {
      const serverVersion = await fetchServerVersion(controller.signal);
      if (
        serverVersion &&
        serverVersion !== "dev" &&
        serverVersion !== __APP_VERSION__
      ) {
        setUpdateAvailable(true);
      }
    };

    const interval = setInterval(check, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    void check();

    return () => {
      controller.abort();
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return updateAvailable;
}
